import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, inject } from '@angular/core';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { Notification } from 'hx-services';
import { SHELL_ICON } from '../shell-icons';

export interface NotificationGroup {
  /** `shell.notes.today` / `shell.notes.yesterday`, or empty — then {@link date} is the label. */
  key: string;
  date?: string;
  items: Notification[];
}

/**
 * The bell of the new header and its panel (the canvas, «Шапка — состояния», 6, 6а, 7): grouped by day, the unread ones marked, «прочитано» without opening, «Прочитать все», the profile for the rest. The data and the
 * actions are those of the old `hc-notifier` — and like it this one has no source yet: the list is empty and «прочитано» goes nowhere until the notifications are wired to the server (a task of its own).
 */
@Component({
  selector: 'app-shell-notifications',
  templateUrl: './shell-notifications.component.html',
  styleUrls: ['./shell-notifications.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, NgFor, DatePipe, TranslocoPipe],
})
export class ShellNotificationsComponent {
  readonly icon = SHELL_ICON;
  readonly profileUrl = `${location.protocol}//profile.${location.hostname.split('.').slice(1).join('.')}/notification`;
  events: Notification[] = [];
  groups: NotificationGroup[] = [];
  unseen = 0;
  unread = 0;
  open = false;
  selected?: Notification;

  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.open && !this.el.nativeElement.contains(event.target as Node)) {
      this.open = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (this.selected) {
      event.preventDefault();
      this.selected = undefined;
    } else if (this.open) {
      event.preventDefault();
      this.open = false;
    }
    this.cdr.markForCheck();
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) {
      this.regroup();
      if (this.events.some(e => !e.seenDate)) {
        this.markAsSeen(this.events.map(e => e.id));
      }
    }
  }

  read(item: Notification): void {
    this.open = false;
    this.markAsRead([item.id]);
    this.selected = item;
  }

  closeSelected(): void {
    this.selected = undefined;
  }

  markAllAsRead(): void {
    this.markAsRead(this.events.map(e => e.id));
  }

  markOne(event: Event, item: Notification): void {
    event.stopPropagation();
    this.markAsRead([item.id]);
  }

  /** The old component's TODO: there is no service for it yet. */
  private markAsSeen(_ids: number[]): void {
  }

  /** The old component's TODO: there is no service for it yet. */
  private markAsRead(_ids: number[]): void {
  }

  private regroup(): void {
    const today = startOfDay(new Date());
    const yesterday = today - 86_400_000;
    const groups: NotificationGroup[] = [];
    for (const e of [...this.events].sort((a, b) => +new Date(b.createDate) - +new Date(a.createDate))) {
      const day = startOfDay(new Date(e.createDate));
      const key = day === today ? 'shell.notes.today' : day === yesterday ? 'shell.notes.yesterday' : '';
      const last = groups[groups.length - 1];
      const date = key ? undefined : new Date(day).toISOString();
      if (last && last.key === key && last.date === date) {
        last.items.push(e);
      } else {
        groups.push({ key, date, items: [e] });
      }
    }
    this.groups = groups;
    this.unseen = this.events.filter(e => !e.seenDate).length;
    this.unread = this.events.filter(e => !e.readDate).length;
  }
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}
